import moment, {Moment} from 'moment-timezone';
import {Component} from 'panel';

import {annotationsClient} from 'common/report/queries/app-api/annotations-client';
import {getMpContext} from 'common/mp-context';
import {track, TrackingEvent, TrackingEventProperty} from 'common/components/line-chart/annotations/tracking';
import {getReportName} from 'common/report/util';

import template from './index.jade';
import css from './index.styl?inline';

const timeItems = [
  `12:00am`,
  `1:00am`,
  `2:00am`,
  `3:00am`,
  `4:00am`,
  `5:00am`,
  `6:00am`,
  `7:00am`,
  `8:00am`,
  `9:00am`,
  `10:00am`,
  `11:00am`,
  `12:00pm`,
  `1:00pm`,
  `2:00pm`,
  `3:00pm`,
  `4:00pm`,
  `5:00pm`,
  `6:00pm`,
  `7:00pm`,
  `8:00pm`,
  `9:00pm`,
  `10:00pm`,
  `11:00pm`,
].map((hour) => ({label: hour}));

export interface CreateAnnotationState {
  /** the date of the annotation */
  date: Moment;
  /** open and closes the calendar component */
  dateInputFocused: boolean;
  /** description of the annotation */
  description: string;
  /** gives user error message when trying to save an annotation and description is blank */
  invalidDescription: boolean;
  /** used to determine if the save annotation button should be disabled or not */
  saving: boolean;
  /** temporary date when user selects different dates on the calendar but has not selected the select date button */
  tmpDate: Moment;
}

export class CreateAnnotationForm extends Component<CreateAnnotationState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'utc-offset': {
        type: `string`,
        description: `utc offset from project, which is only necessary if you want incomplete interval zoning`,
      },
      date: {
        type: `string`,
        description: `date of annotation with format "MMM D, YYYY HH:mm:ss"`,
      },
    };
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      template,
      helpers: {
        getTimeItems: () => timeItems,

        getTimeInput: () => {
          return this.querySelector(`.annotation-time-input`);
        },

        getFormattedTime: (format, date = this.state.date) => {
          return date.format(format);
        },

        descriptionTextareaInserted: (vnode) =>
          requestAnimationFrame(() => {
            vnode.elm.focus();
            vnode.elm.select();
          }),

        handleTimeSelectChange: (ev) => {
          const newDate = moment(ev.detail.item.label, `h:mma`);
          const date = this.state.date.hour(newDate.hour()).minute(newDate.minute());
          this.update({date});
        },

        handleDateSelected: (ev) => {
          this.update({tmpDate: moment(ev.detail.date)});
        },

        handleSelectDateButtonClicked: () => {
          const newDate = moment(this.state.tmpDate);
          const date = this.state.date.year(newDate.year()).month(newDate.month()).date(newDate.date());
          this.update({date, dateInputFocused: false});
        },

        handleDateSelectChange: ({detail}) => {
          this.update({dateInputFocused: detail.isOpen});
        },

        updateDescription: (ev) => {
          this.update({
            description: ev.target.value,
            invalidDescription: false,
          });
        },

        cancelCreation: () => {
          this.dispatchEvent(new CustomEvent(`closeAnnotationPopup`));
        },

        saveAnnotation: async () => {
          if (!this.state.description) {
            this.update({invalidDescription: true});
            return;
          }

          const annotationProjectTime = this.toAbsoluteProjectTime(this.state.date);

          this.update({saving: true});
          const annotation = await annotationsClient.createAnnotation({
            projectId: getMpContext().projectId,
            annotation: {
              date: annotationProjectTime.format(`YYYY-MM-DD HH:mm:ss`),
              description: this.state.description,
            },
            workspaceId: getMpContext().workspaceId,
          });

          this.dispatchEvent(new CustomEvent(`annotationCreated`, {detail: annotation}));

          track(TrackingEvent.Add, {
            [TrackingEventProperty.AnnotationText]: this.state.description,
            [TrackingEventProperty.AnnotationTimestamp]: this.state.date.format(`YYYY-MM-DD HH:mm:ss`),
            [TrackingEventProperty.ReportName]: getReportName(),
          });
        },
      },
    };
  }

  attributeChangedCallback(attrName, oldVal, newVal) {
    super.attributeChangedCallback(attrName, oldVal, newVal);

    if (attrName === `date` && newVal) {
      const date = moment(newVal, `MMM D, YYYY HH:mm:ss`);
      this.update({date});
    }
  }

  /** using utc-offset, convert date to project time */
  toAbsoluteProjectTime(date) {
    const projectTzOffset = getMpContext().utcOffset;
    return projectTzOffset ? moment(date).clone().utcOffset(projectTzOffset, true) : date;
  }
}

customElements.define(`create-annotation-form`, CreateAnnotationForm);
